import { Info } from "lucide-react";
import { OrderQtyBreakdownEntry } from "@/lib/api";
import HoverPopover from "@/components/ui/HoverPopover";

interface Props {
  total: number;
  breakdown: OrderQtyBreakdownEntry[];
}

function formatQty(n: number): string {
  return n.toLocaleString('de-DE');
}

function formatFactor(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toLocaleString('de-DE', { maximumFractionDigits: 3 });
}

export default function OrderQtyBreakdownTooltip({ total, breakdown }: Props) {
  const sumContributions = breakdown.reduce((acc, e) => acc + e.contribution, 0);
  const moqAdjusted = total > sumContributions;

  return (
    <HoverPopover
      trigger={
        <Info className="h-3.5 w-3.5 text-ink-soft hover:text-ink-muted cursor-help" />
      }
      panelClassName="w-[34rem] max-w-[92vw] rounded-card border border-line bg-surface p-3 text-left"
    >
      <span className="block font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft mb-2">
        Bedarf aus Fertigartikeln
      </span>
      <table className="w-full text-xs border-separate border-spacing-x-4 border-spacing-y-0">
        <colgroup>
          <col />
          <col className="w-24" />
          <col className="w-16" />
          <col className="w-24" />
        </colgroup>
        <thead>
          <tr className="text-ink-soft">
            <th className="text-left font-medium pb-1">SKU</th>
            <th className="text-right font-medium pb-1">Menge</th>
            <th className="text-right font-medium pb-1">Faktor</th>
            <th className="text-right font-medium pb-1">Beitrag</th>
          </tr>
        </thead>
        <tbody className="text-ink-muted">
          {breakdown.map((entry) => (
            <tr key={entry.parentSku} className="align-top">
              <td className="py-1 font-mono text-[11px] border-t border-surface-3">
                <div className="truncate">{entry.parentSku}</div>
                {entry.parentName && (
                  <div className="text-[10px] text-ink-soft font-sans truncate">{entry.parentName}</div>
                )}
              </td>
              <td className="py-1 text-right tabular-nums border-t border-surface-3">{formatQty(entry.parentQty)}</td>
              <td className="py-1 text-right tabular-nums border-t border-surface-3">{formatFactor(entry.factor)}</td>
              <td className="py-1 text-right tabular-nums font-semibold border-t border-surface-3">{formatQty(entry.contribution)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={3} className="pt-1 text-right font-semibold border-t border-line text-ink">Summe</td>
            <td className="pt-1 text-right tabular-nums font-semibold border-t border-line text-ink">{formatQty(sumContributions)}</td>
          </tr>
          {moqAdjusted && (
            <tr>
              <td colSpan={3} className="pt-1 text-right text-[11px] text-ink-soft">MOQ</td>
              <td className="pt-1 text-right tabular-nums text-[11px] text-ink-soft">{formatQty(total)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </HoverPopover>
  );
}
